/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2022-2024 (original work) Open Assessment Technologies SA
 *
 */
import { log } from '@/core/utils';
import { writable, get } from 'svelte/store';
import { getUserTokenData, getCurrentUser, logout as authLogout } from '@/services/authService.js';
import { initialize } from '@/services/analyticsService.js';
import { getMetadataSchema } from '@/services/classificationService.js';
import { mapMetadataToClient } from '@/validators/metadataValidator';
import { logActivity } from '@/services/activityLogService';
import { configurationStore } from '@/stores/tenantConfiguration.store';
import * as authService from '@/services/authService';

const defaultUserState = {
    isLoginNotExpected: false,
    birthdate: '',
    addresses: [],
    metadata: {},
    workingProfiles: []
};

function createAuthStore() {
    const userStore = writable(defaultUserState);
    const { subscribe, set, update } = userStore;

    return {
        get() {
            return get(userStore);
        },
        subscribe,
        set,
        update,
        isSessionOneTimeLogin() {
            return !!get(userStore).sessionId;
        },
        async updateInfo(overrides = {}) {
            await this.updateTokenData();
            const currentUser = get(userStore);
            const userId = currentUser?.userId;
            if (!userId) {
                return;
            }
            const { tenantId } = configurationStore.getCurrentTenant();
            const isMetadataEnabled = get(configurationStore).metadataEnabled;
            const metadataSchema = isMetadataEnabled
                ? await getMetadataSchema(tenantId, 'user').then(mapMetadataToClient)
                : {};
            const {
                _id,
                active,
                email,
                emailVerified,
                login,
                name,
                language,
                features = [],
                permissions = [],
                restrictions = [],
                allowedRolesForAssignment = [],
                birthdate,
                addresses,
                isExternalUser,
                readonlyFields,
                metadata = {},
                sessionId,
                workingProfiles,
                workingProfileName,
                protected: isProtected
            } = await getCurrentUser();
            const metadataToSend = isMetadataEnabled ? { ...metadata, ...overrides.metadata } : null;
            this.update(s => ({
                ...s,
                id: _id,
                active,
                email,
                emailVerified,
                login,
                name,
                language,
                features,
                permissions,
                restrictions,
                allowedRolesForAssignment,
                birthdate,
                addresses: addresses || s.addresses || defaultUserState.addresses,
                isExternalUser,
                readonlyFields,
                metadata: metadataToSend,
                metadataSchema,
                sessionId,
                workingProfiles,
                workingProfileName,
                protected: isProtected
            }));
            initialize(
                this.get(),
                configurationStore.getCurrentTenant(),
                configurationStore.getUserpilotConfiguration()
            );
        },
        async updateTokenData() {
            try {
                const { userId, roles, hierarchy } = await getUserTokenData();
                update(s => ({
                    ...s,
                    userId,
                    roles,
                    hierarchy
                }));
            } catch (err) {
                log.warn("Couldn't update token data:", err.message);
                set(null);
            }
        },
        async logout() {
            if (this.get()) {
                await logActivity({ action: 'logout' });
            }
            await authLogout();
            this.set(null);
        },
        /**
         * Get hierarchyId and organizationId from user store
         * @returns {{hierarchyId: string, organizationId: string}}
         */
        getHierarchy() {
            const hierarchy = get(userStore)?.hierarchy ?? {};
            const hierarchyId = Object.keys(hierarchy)[0];
            const organizationId = hierarchy[hierarchyId];
            return {
                hierarchyId,
                organizationId
            };
        },
        getCurrentWorkingProfile() {
            const { workingProfileName } = get(userStore);
            return get(userStore)?.workingProfiles?.find(wp => wp.name === workingProfileName);
        },
        /**
         * Switch to another working profile.
         * This is like loging in again, but without the need of the user to enter their credentials.
         * @param {string} workingProfileName
         * @async
         * @returns {Promise<void|error>}
         */
        async changeWorkingProfile(workingProfileName) {
            if (workingProfileName !== get(userStore)?.workingProfileName) {
                try {
                    await authService.getWorkingProfileToken(workingProfileName);
                    await this.updateInfo();
                    return;
                } catch (e) {
                    log.error('Error switching profile', e);
                    return e;
                }
            }
        },
        getCurrentAllowedRolesForAssignment() {
            return get(userStore)?.allowedRolesForAssignment;
        }
    };
}

export const user = createAuthStore();
